import {
  Component,
  ElementRef,
  PLATFORM_ID,
  inject,
  viewChild,
} from "@angular/core";
import { RouterLink, Router } from "@angular/router";
import { isPlatformBrowser } from "@angular/common";
import { DataService } from "./data";
import { TourCard } from "./tour-card";
import { Icon } from "./icon";
import { Reveal } from "./reveal";
import { Tilt } from "./tilt";
import { Gem } from "./gem";
import { PlaneFlyby } from "./plane-flyby";

const TESTIMONIALS = [
  {
    initials: "QY",
    name: "Quin Yang",
    role: "Content Creator, Melbourne",
    quote:
      "Sri Lanka exceeded every expectation. The route through the highlands to Ella, especially the train - I would book that week again tomorrow.",
  },
  {
    initials: "MR",
    name: "Marcus Reid",
    role: "Product Manager, London",
    quote:
      "Our guide adjusted the whole southern leg on day two because we mentioned we liked slower mornings. That's the kind of planning you don't get from a template tour.",
  },
  {
    initials: "AS",
    name: "Amara Silva",
    role: "Physician, Toronto",
    quote:
      "The Panoramic Tours route was genuinely nine days of highlights, not filler. Yala at dawn and a Bentota sunset on the same trip felt impossible until it wasn't.",
  },
];

const GEM_FACTS = [
  {
    icon: "gem",
    label:
      "One of the oldest gem-trading nations on earth, documented over 2,000 years",
  },
  {
    icon: "star",
    label:
      "Home of the Blue Belt of Ceylon, the gravel seam that yields the world's finest blue sapphires",
  },
  {
    icon: "globe",
    label:
      'Marco Polo himself wrote of an island whose king owned "the finest ruby that ever was seen"',
  },
];

@Component({
  selector: "app-home",
  imports: [RouterLink, TourCard, Icon, Reveal, Tilt, Gem, PlaneFlyby],
  template: `
    <!-- Hero -->
    <section
      class="relative min-h-screen flex flex-col overflow-hidden surface-ink"
    >
      <img
        src="/hero-bg.png"
        alt="Sigiriya Rock Fortress at dusk"
        class="absolute inset-0 w-full h-full object-cover scale-105 animate-pulse-slow"
        referrerpolicy="no-referrer"
      />
      <div
        class="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/25 to-ink/60"
      ></div>
      <app-plane-flyby />

      <div
        class="container relative z-10 text-paper flex-1 flex flex-col justify-center pt-32 pb-24"
      >
        <div class="max-w-4xl">
          <div
            class="flex items-center gap-3 mb-8"
            appReveal
            [revealDelay]="0.1"
          >
            <span
              class="text-saffron font-bold tracking-[0.3em] uppercase text-xs"
              >Sri Lanka</span
            >
            <div class="h-px w-12 bg-saffron/50"></div>
            <span class="text-paper/60 text-xs uppercase tracking-widest"
              >The Teardrop of the Indian Ocean</span
            >
          </div>

          <h1
            class="text-6xl sm:text-7xl md:text-9xl font-serif mb-8 leading-[0.85] tracking-tight"
            appReveal
            [revealDelay]="0.2"
          >
            Step Into <br />
            <span class="text-saffron italic font-light">Serenity</span>
          </h1>

          <p
            class="text-lg md:text-xl text-paper/75 mb-12 leading-relaxed max-w-xl"
            appReveal
            [revealDelay]="0.3"
          >
            Discover ancient wonders and pristine shores. We route journeys that
            resonate with your pace, from the misty hills of Ella to the golden
            sands of Mirissa.
          </p>

          <div class="flex flex-wrap gap-4" appReveal [revealDelay]="0.4">
            <a routerLink="/tours" class="btn-saffron">
              Start Your Journey <app-icon name="compass" [size]="17" />
            </a>
            <a routerLink="/gallery" class="btn-outline-light">
              See the Island <app-icon name="play" [size]="15" />
            </a>
          </div>
        </div>
      </div>

      <!-- Search -->
      <div class="container mx-auto relative z-20 pb-12 lg:pb-0 lg:h-0">
        <div
          class="lg:absolute lg:bottom-[3.5rem] lg:left-1/2 lg:-translate-x-1/2 lg:translate-y-1/2 w-full max-w-5xl glass rounded-[2rem] p-2 flex flex-col md:flex-row items-stretch gap-2"
          appReveal
          [revealDelay]="0.5"
        >
          <div class="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
            <div
              class="p-4 rounded-2xl hover:bg-white/40 transition-colors group"
            >
              <label
                for="destination"
                class="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-ink/40 mb-2 group-hover:text-vermilion transition-colors"
              >
                <app-icon name="pin" [size]="12" /> Destination
              </label>
              <input
                #destInput
                id="destination"
                type="text"
                placeholder="Try 'Ella' or 'coast'"
                class="bg-transparent border-none p-0 text-ink font-semibold text-sm focus:outline-none focus:ring-0 placeholder:text-ink/25 w-full"
                (keydown.enter)="search(destInput.value)"
              />
            </div>
            <div
              class="p-4 rounded-2xl hover:bg-white/40 transition-colors group"
            >
              <label
                for="budget"
                class="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-ink/40 mb-2 group-hover:text-vermilion transition-colors"
              >
                <app-icon name="wallet" [size]="12" /> Budget
              </label>
              <select
                #budgetSelect
                id="budget"
                class="bg-transparent border-none p-0 text-ink font-semibold text-sm focus:outline-none focus:ring-0 w-full appearance-none"
              >
                <option value="">Any budget</option>
                <option value="under300">Under &#36;300</option>
                <option value="300-700">&#36;300 – &#36;700</option>
                <option value="700plus">&#36;700+</option>
              </select>
            </div>
            <div
              class="p-4 rounded-2xl hover:bg-white/40 transition-colors group"
            >
              <label
                for="travelers"
                class="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-ink/40 mb-2 group-hover:text-vermilion transition-colors"
              >
                <app-icon name="users" [size]="12" /> Travelers
              </label>
              <select
                #travSelect
                id="travelers"
                class="bg-transparent border-none p-0 text-ink font-semibold text-sm focus:outline-none focus:ring-0 w-full appearance-none"
              >
                <option>1 Traveler</option>
                <option selected>2 Travelers</option>
                <option>3 Travelers</option>
                <option>4+ Travelers</option>
              </select>
            </div>
          </div>
          <button
            type="button"
            (click)="search(destInput.value, budgetSelect.value)"
            class="bg-gradient-to-br from-[#d6472c] to-vermilion text-paper w-full md:w-16 shrink-0 flex items-center justify-center rounded-full md:rounded-2xl hover:shadow-lg hover:shadow-vermilion/30 transition-all"
          >
            <app-icon name="search" [size]="19" />
          </button>
        </div>
      </div>
    </section>

    <!-- Ratnadeepa: Island of Gems (signature 3D moment) -->
    <section class="grain relative py-32 sm:py-40 overflow-hidden surface-ink">
      <div
        class="mesh-blob mesh-blob-a w-[520px] h-[520px] -top-40 -left-40 bg-saffron/20"
      ></div>
      <div
        class="mesh-blob mesh-blob-b w-[420px] h-[420px] bottom-0 right-0 bg-vermilion/15"
      ></div>
      <div
        class="mesh-blob mesh-blob-a w-[300px] h-[300px] top-1/3 right-1/4 bg-teal/10"
      ></div>

      <div class="container relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-2 items-center gap-16">
          <div
            class="order-2 lg:order-1 space-y-7 text-paper"
            appReveal
            [revealDelay]="0.05"
          >
            <span class="route-code text-saffron text-xs"
              >ANCIENT NAME · &#34;RATNADEEPA&#34;</span
            >
            <h2
              class="text-5xl md:text-6xl font-serif leading-[0.95] tracking-tight"
            >
              The Island
              <span class="italic font-light text-paper/50">of Gems.</span>
            </h2>
            <p class="text-paper/70 leading-relaxed text-lg max-w-lg">
              Long before "teardrop," Sri Lanka carried an older name across
              Sanskrit and Pali trade routes -
              <em class="text-saffron not-italic font-medium">Ratnadeepa</em>,
              the island of gems. The gravel seams beneath Ratnapura still yield
              sapphires found in this quality nowhere else on earth.
            </p>
            <div class="space-y-4 pt-2">
              @for (fact of gemFacts; track fact.label) {
                <div class="flex items-start gap-3">
                  <app-icon
                    [name]="fact.icon"
                    [size]="16"
                    class="text-saffron mt-1 shrink-0"
                  />
                  <p class="text-paper/60 text-sm leading-relaxed">
                    {{ fact.label }}
                  </p>
                </div>
              }
            </div>
          </div>

          <div class="order-1 lg:order-2" appReveal="scale">
            <div
              appTilt
              [appTiltMax]="6"
              class="relative aspect-square max-w-md mx-auto glass-ink rounded-[2.5rem] overflow-hidden"
            >
              <app-gem class="absolute inset-0" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Popular Tours -->
    <section class="py-32 bg-paper">
      <div class="container">
        <div
          class="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8"
        >
          <div class="max-w-2xl" appReveal="fade">
            <div class="flex items-center gap-2 mb-4">
              <div class="w-8 h-[2px] bg-vermilion"></div>
              <span
                class="text-vermilion font-bold uppercase tracking-[0.2em] text-[10px]"
                >Curated Experiences</span
              >
            </div>
            <h2 class="text-5xl md:text-6xl font-serif leading-tight">
              Popular Sri Lankan <br />
              <span class="italic font-light text-ink/35">Escapes</span>
            </h2>
          </div>
          <p
            class="text-ink/55 max-w-md leading-relaxed"
            appReveal="fade"
            [revealDelay]="0.1"
          >
            Six routes across the teardrop island - from a single day in Colombo
            to a nine-day circuit through the Cultural Triangle, the hill
            country and the coast.
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          @for (tour of featured(); track tour.id; let i = $index) {
            <app-tour-card [tour]="tour" appReveal [revealDelay]="i * 0.1" />
          }
        </div>

        <div class="mt-16 text-center">
          <a
            routerLink="/tours"
            class="group inline-flex items-center gap-3 font-bold hover:text-vermilion transition-colors"
          >
            View All Six Routes
            <div
              class="w-10 h-10 rounded-full border border-sand flex items-center justify-center group-hover:border-vermilion group-hover:bg-vermilion group-hover:text-paper transition-all"
            >
              <app-icon name="arrow-right" [size]="15" />
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- Welcome / Story -->
    <section class="grain relative py-40 overflow-hidden surface-ink">
      <div class="absolute inset-0 opacity-20">
        <img
          src="/welcome-bg.png"
          alt=""
          class="w-full h-full object-cover grayscale"
          referrerpolicy="no-referrer"
        />
      </div>
      <div
        class="mesh-blob mesh-blob-b w-[400px] h-[400px] top-0 right-0 bg-teal/15"
      ></div>

      <div class="container relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-2 items-center gap-20">
          <div class="relative" appReveal="scale">
            <div
              appTilt
              [appTiltMax]="5"
              class="overflow-hidden rounded-[2.5rem] border border-paper/10 shadow-2xl"
            >
              <img
                src="/welcome-side.png"
                alt="Illustrated map of Sri Lanka"
                class="w-full h-auto"
                referrerpolicy="no-referrer"
              />
            </div>
            <div
              class="absolute -bottom-8 -right-8 glass rounded-2xl shadow-2xl p-7 hidden md:block"
            >
              <div class="route-code text-vermilion text-4xl mb-1">11+</div>
              <div
                class="text-ink/60 text-[10px] uppercase tracking-widest font-bold"
              >
                Years Routing Trips
              </div>
            </div>
          </div>

          <div class="space-y-8 text-paper" appReveal [revealDelay]="0.1">
            <div
              class="inline-block px-4 py-1 rounded-full border border-paper/20 text-[10px] uppercase tracking-widest font-bold text-saffron"
            >
              Our Story
            </div>
            <h2
              class="text-5xl md:text-7xl font-serif leading-[0.95] tracking-tight"
            >
              Welcome To <br />
              <span class="italic font-light text-paper/50"
                >Sri Lankan Explorer</span
              >
            </h2>
            <p class="text-paper/70 leading-relaxed text-lg max-w-xl">
              Sri Lanka, formerly Ceylon, sits south of India in the Indian
              Ocean - a single island holding rainforest, arid plains,
              tea-terraced highlands and sun-baked coastline, all inside a day's
              drive of each other.
            </p>
            <div class="flex flex-wrap items-center gap-6 pt-2">
              <a routerLink="/about" class="btn-saffron">Read Our Story</a>
              <div class="flex -space-x-3">
                @for (t of testimonials; track t.initials) {
                  <div
                    class="w-11 h-11 rounded-full border-2 border-ink bg-teal flex items-center justify-center text-paper text-xs font-bold"
                  >
                    {{ t.initials }}
                  </div>
                }
                <div
                  class="w-11 h-11 rounded-full border-2 border-ink bg-saffron flex items-center justify-center text-ink text-[10px] font-bold"
                >
                  +2k
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials -->
    <section class="py-32 bg-paper">
      <div class="container">
        <div class="text-center max-w-3xl mx-auto mb-24" appReveal="fade">
          <div
            class="inline-block px-4 py-1 rounded-full bg-paper-soft text-[10px] uppercase tracking-widest font-bold text-vermilion mb-6"
          >
            Testimonials
          </div>
          <h2 class="text-5xl md:text-6xl font-serif mb-6 tracking-tight">
            Voices of Our
            <span class="italic font-light text-ink/35">Voyagers</span>
          </h2>
          <p class="text-ink/55 text-lg">
            Real stories from travelers we've routed across the island.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          @for (t of testimonials; track t.name; let i = $index) {
            <div
              appTilt
              [appTiltMax]="5"
              class="relative p-10 rounded-[1.75rem] bg-paper-soft border border-sand/50 hover:border-vermilion/30 hover:shadow-2xl transition-shadow duration-500"
              appReveal
              [revealDelay]="i * 0.1"
            >
              <app-icon
                name="quote"
                [size]="26"
                class="text-vermilion/40 mb-6"
              />
              <div class="flex text-saffron mb-6 gap-0.5">
                @for (s of [1, 2, 3, 4, 5]; track s) {
                  <app-icon name="star" [size]="13" />
                }
              </div>
              <p
                class="text-ink/70 leading-relaxed mb-10 font-serif text-lg italic"
              >
                "{{ t.quote }}"
              </p>
              <div class="flex items-center gap-4 pt-6 border-t border-sand/60">
                <div
                  class="w-12 h-12 rounded-2xl bg-ink flex items-center justify-center text-saffron font-bold text-sm shrink-0"
                >
                  {{ t.initials }}
                </div>
                <div>
                  <h4 class="font-bold text-ink text-sm">{{ t.name }}</h4>
                  <p
                    class="text-ink/40 text-[10px] uppercase tracking-widest font-bold"
                  >
                    {{ t.role }}
                  </p>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- Gallery Preview -->
    <section class="py-32 bg-paper-soft">
      <div class="container">
        <div
          class="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8"
        >
          <div class="max-w-2xl" appReveal="fade">
            <div class="flex items-center gap-2 mb-4">
              <div class="w-8 h-[2px] bg-vermilion"></div>
              <span
                class="text-vermilion font-bold uppercase tracking-[0.2em] text-[10px]"
                >Visual Journey</span
              >
            </div>
            <h2 class="text-5xl md:text-6xl font-serif leading-tight">
              Captured
              <span class="italic font-light text-ink/35">Moments</span>
            </h2>
          </div>
          <a routerLink="/gallery" class="btn-vermilion"
            >Explore Full Gallery</a
          >
        </div>

        <div class="grid grid-cols-1 md:grid-cols-4 gap-5 h-auto md:h-[760px]">
          <div
            class="md:col-span-1 md:row-span-1 rounded-[1.75rem] overflow-hidden group relative h-64 md:h-auto"
            appReveal="fade"
          >
            <img
              src="./home-grid-1.png"
              alt="Sri Lanka waterfall"
              class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
              referrerpolicy="no-referrer"
            />
            <div
              class="absolute inset-0 bg-ink/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            >
              <app-icon name="zoom-in" [size]="26" class="text-paper" />
            </div>
          </div>
          <div
            class="md:col-span-3 md:row-span-1 rounded-[1.75rem] overflow-hidden group relative h-64 md:h-auto"
            appReveal="fade"
            [revealDelay]="0.05"
          >
            <img
              src="./home-grid-2.png"
              alt="Buddha statues, Colombo"
              class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
              referrerpolicy="no-referrer"
            />
            <div
              class="absolute inset-0 bg-ink/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            >
              <app-icon name="zoom-in" [size]="26" class="text-paper" />
            </div>
          </div>
          <div
            class="md:col-span-1 md:row-span-1 rounded-[1.75rem] overflow-hidden group relative h-64 md:h-auto"
            appReveal="fade"
            [revealDelay]="0.1"
          >
            <img
              src="./home-grid-3.png"
              alt="Temple at night, Kandy"
              class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
              referrerpolicy="no-referrer"
            />
            <div
              class="absolute inset-0 bg-ink/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            >
              <app-icon name="zoom-in" [size]="26" class="text-paper" />
            </div>
          </div>
          <div
            class="md:col-span-2 md:row-span-1 rounded-[1.75rem] overflow-hidden group relative h-64 md:h-auto"
            appReveal="fade"
            [revealDelay]="0.15"
          >
            <img
              src="./home-grid-4.png"
              alt="Infinity pool, south coast"
              class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
              referrerpolicy="no-referrer"
            />
            <div
              class="absolute inset-0 bg-ink/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            >
              <app-icon name="zoom-in" [size]="26" class="text-paper" />
            </div>
          </div>
          <div
            class="md:col-span-1 md:row-span-1 rounded-[1.75rem] overflow-hidden group relative h-64 md:h-auto"
            appReveal="fade"
            [revealDelay]="0.2"
          >
            <img
              src="./home-grid-5.png"
              alt="Train ride through tea country"
              class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
              referrerpolicy="no-referrer"
            />
            <div
              class="absolute inset-0 bg-ink/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            >
              <app-icon name="zoom-in" [size]="26" class="text-paper" />
            </div>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class Home {
  private dataService = inject(DataService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  tours = this.dataService.getTours();
  testimonials = TESTIMONIALS;
  gemFacts = GEM_FACTS;

  featured() {
    return this.tours().slice(0, 4);
  }

  search(destination: string, budget?: string) {
    if (!isPlatformBrowser(this.platformId)) return;
    const queryParams: Record<string, string> = {};
    if (destination?.trim()) queryParams["q"] = destination.trim();
    if (budget) queryParams["budget"] = budget;
    this.router.navigate(["/tours"], { queryParams });
  }
}
